import { getToken, isSupported, onMessage } from 'firebase/messaging';

import { AppError } from '@/shared/lib';
import {
  registerServiceWorker,
  waitUntilActive,
} from '@/shared/lib/service-worker';

import { ERROR_CODE, ERROR_MESSAGE } from '@/feature/alert/constant';
import { isIOSPWA } from '@/feature/alert/lib';

import { sendFCMToken } from '@/apis';

import { messaging } from './firebase-config';

export class PushNotificationManager {
  static async registerServiceWorker() {
    await this.#assertSupport();

    try {
      const registration = await registerServiceWorker(
        '/firebase-messaging-sw.js'
      );
      await waitUntilActive(registration);
      return registration;
    } catch (error) {
      throw new AppError(
        ERROR_CODE.SW_REGISTER_FAILED,
        ERROR_MESSAGE.SW_REGISTER_FAILED
      );
    }
  }

  static async #assertSupport() {
    const isFcmSupported = await isSupported().catch(() => false);

    if (!isFcmSupported) {
      throw new AppError(
        ERROR_CODE.SW_ENV_UNSUPPORTED,
        ERROR_MESSAGE.SW_ENV_UNSUPPORTED
      );
    }
  }

  static async ensurePermission() {
    const current = Notification.permission;

    if (current === 'granted') return;
    if (current === 'denied') {
      const message = isIOSPWA()
        ? ERROR_MESSAGE.PERMISSION_DENIED_IOS
        : ERROR_MESSAGE.PERMISSION_DENIED_ANDROID;

      throw new AppError(ERROR_CODE.PERMISSION_BLOCKED, message);
    }

    const result = await Notification.requestPermission();

    if (result === 'granted') {
      return;
    }

    throw new AppError(ERROR_CODE.PERMISSION_JUST_DENIED);
  }

  static async issueToken(registration) {
    try {
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      return token;
    } catch (error) {
      throw new AppError(
        ERROR_CODE.FCM_TOKEN_ISSUE_FAILED,
        ERROR_MESSAGE.FCM_TOKEN_ISSUE_FAILED
      );
    }
  }

  static async syncWithServer(token, deviceType) {
    try {
      await sendFCMToken(token, deviceType);
    } catch (error) {
      throw new AppError(
        ERROR_CODE.FCM_TOKEN_SYNC_FAILED,
        ERROR_MESSAGE.FCM_TOKEN_SYNC_FAILED
      );
    }
  }

  static onForegroundMessage(callback) {
    onMessage(messaging, (payload) => {
      callback();
    });
  }
}
